// Checkout form UI: delivery, recipient, submit
import { getTg } from '../../core/telegram.js';
import { apiFetch, apiFetchJSON } from '../../core/api.js';
import { flashAppNotice } from '../../core/notice.js';
import { showView } from '../../app/navigation.js';
import { clearCart, loadCart } from '../cart/cart.js';
import { loadMyOrders } from '../orders/list.js';
import { clearBuildOrderDraft, getBuildOrderDraft } from '../../app/build-draft.js';
import { isBuildCheckoutMode, applyCheckoutModeUi, enterCatalogCheckout } from './mode.js';
import {
  buildSubmitErrorMessage,
  recordBuildRequestSubmit,
  setBuildSuccessPhase,
  waitUntilMinElapsed
} from '../build/forms.js';
import {
  bindCheckoutNoteCardExpansion,
  refreshCheckoutOrderSummary,
  refreshCheckoutPaymentSummary
} from './summary.js';
import {
  getCheckoutCdekSnapshot,
  initCheckoutCdek,
  isCheckoutCdekPickerMode,
  onCheckoutCdekChange,
  refreshCheckoutCdekQuote,
  resetCheckoutCdekSelection
} from './cdek.js';
import type {
  ApiResponse,
  CheckoutOrderPayload,
  CheckoutOrderResponse,
  CheckoutPaymentInstructions,
  CheckoutPaymentMethod,
  DeliveryMethod,
  PaymentMethod
} from '../../types/api.js';

const SELF_PICKUP_CITY = 'Санкт-Петербург';
const CHECKOUT_PAYMENT_MIN_MS = 900;
const CHECKOUT_CLAIM_MIN_MS = 700;
const BUILD_ORDER_MIN_MS = 2000;
let checkoutSubmitRequestId = '';
let checkoutPaymentOrderId = '';
let checkoutPaymentLoadingTimer: ReturnType<typeof setInterval> | null = null;

type CheckoutPaymentPhase = 'loading' | 'ready' | 'claiming' | 'claimed' | 'error';

function shortCheckoutOrderId(orderId: string): string {
  const raw = String(orderId || '').trim();
  if (!raw) return '—';
  const tail = raw.split('-').pop() || raw;
  return tail.length > 8 ? tail.slice(-8) : tail;
}

function formatRubUi(amount: number): string {
  return `${Math.round(amount).toLocaleString('ru-RU')} ₽`;
}

async function copyCheckoutText(
  text: string,
  btn?: HTMLButtonElement | null
): Promise<boolean> {
  const value = String(text || '').trim();
  if (!value) return false;
  let copied = false;
  try {
    await navigator.clipboard.writeText(value);
    copied = true;
  } catch {
    /* fallback */
  }
  if (!copied) {
    try {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.setAttribute('readonly', 'true');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      copied = true;
    } catch {
      copied = false;
    }
  }
  if (copied && btn) {
    const prev = btn.textContent;
    btn.classList.add('checkout-payment-copy-btn--copied');
    btn.textContent = 'Скопировано';
    window.setTimeout(() => {
      btn.classList.remove('checkout-payment-copy-btn--copied');
      if (prev) btn.textContent = prev;
    }, 1600);
  }
  return copied;
}

function escapePaymentHtml(text: string): string {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function splitPaymentMethodTitle(title: string): { main: string; bank: string } {
  const raw = String(title || '').trim();
  const match = raw.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) return { main: raw, bank: '' };
  return { main: match[1].trim(), bank: match[2].trim() };
}

function renderCheckoutPaymentMethodCard(method: CheckoutPaymentMethod, primary: boolean): string {
  const cardClass = primary
    ? 'checkout-payment-method-card checkout-payment-method-card--primary'
    : 'checkout-payment-method-card';
  if (method.type === 'sbp' && method.phone) {
    const phone = escapePaymentHtml(method.phone);
    const { main, bank } = splitPaymentMethodTitle(method.title);
    const titleMain = escapePaymentHtml(main);
    const titleBank = bank ? escapePaymentHtml(bank) : '';
    return (
      `<article class="${cardClass}">` +
      `<div class="checkout-payment-method-head">` +
      `<img class="checkout-payment-sbp-icon" src="/assets/sbp-logo.svg" width="48" height="48" alt="" decoding="async" />` +
      `<div class="checkout-payment-method-titles">` +
      `<p class="checkout-payment-method-title">${titleMain}</p>` +
      (titleBank ? `<p class="checkout-payment-method-bank">${titleBank}</p>` : '') +
      `</div>` +
      `</div>` +
      `<div class="checkout-payment-method-value-row">` +
      `<span class="checkout-payment-method-value">${phone}</span>` +
      `<button type="button" class="checkout-payment-copy-btn" data-copy="${phone}">Скопировать</button>` +
      `</div>` +
      `<p class="checkout-payment-method-meta">Платежи → СБП → по номеру телефона</p>` +
      `</article>`
    );
  }
  const title = escapePaymentHtml(method.title);
  if (method.type === 'card' && method.cardNumber) {
    const card = escapePaymentHtml(method.cardNumber);
    const meta = [method.cardHolder, method.cardBank].filter(Boolean).join(' · ');
    return (
      `<article class="${cardClass}">` +
      `<p class="checkout-payment-method-title">${title}</p>` +
      `<p class="checkout-payment-method-value">${card}</p>` +
      (meta ? `<p class="checkout-payment-method-meta">${escapePaymentHtml(meta)}</p>` : '') +
      `<button type="button" class="checkout-payment-copy-btn" data-copy="${card.replace(/\s+/g, '')}">Копировать карту</button>` +
      `</article>`
    );
  }
  if (method.type === 'link' && method.link) {
    const link = escapePaymentHtml(method.link);
    return (
      `<article class="${cardClass}">` +
      `<p class="checkout-payment-method-title">${title}</p>` +
      `<button type="button" class="checkout-payment-open-link-btn" data-open-link="${link}">Открыть ссылку</button>` +
      `</article>`
    );
  }
  return '';
}

function renderCheckoutPaymentInstructions(instructions: CheckoutPaymentInstructions | undefined): void {
  const container = document.getElementById('checkout-payment-requisites');
  const commentRow = document.getElementById('checkout-payment-comment-row');
  const commentEl = document.getElementById('checkout-payment-comment');
  const extraEl = document.getElementById('checkout-payment-extra');
  const noReq = document.getElementById('checkout-payment-no-requisites');
  const guide = document.getElementById('checkout-payment-guide');

  const methods = instructions?.methods || [];
  if (container) {
    container.innerHTML = methods
      .map((method, index) => renderCheckoutPaymentMethodCard(method, index === 0))
      .join('');
    container.onclick = (event) => {
      const target = event.target as HTMLElement | null;
      const copyBtn = target?.closest('[data-copy]') as HTMLButtonElement | null;
      if (copyBtn) {
        void copyCheckoutText(String(copyBtn.getAttribute('data-copy') || ''), copyBtn);
        return;
      }
      const linkBtn = target?.closest('[data-open-link]') as HTMLElement | null;
      if (linkBtn) {
        openTelegramBotLink(String(linkBtn.getAttribute('data-open-link') || ''));
      }
    };
  }

  const comment = String(instructions?.transferComment || '').trim();
  if (commentRow) commentRow.hidden = !comment;
  if (commentEl) commentEl.textContent = comment;

  const extra = String(instructions?.extra || '').trim();
  if (extraEl) {
    extraEl.hidden = !extra;
    extraEl.textContent = extra;
  }

  if (noReq) noReq.hidden = methods.length > 0;
  if (guide) guide.hidden = methods.length === 0;
}

function stopCheckoutPaymentLoadingAnimation(): void {
  if (checkoutPaymentLoadingTimer) {
    clearInterval(checkoutPaymentLoadingTimer);
    checkoutPaymentLoadingTimer = null;
  }
}

function startCheckoutPaymentLoadingAnimation(): void {
  stopCheckoutPaymentLoadingAnimation();
  const steps = Array.from(
    document.querySelectorAll<HTMLElement>('.checkout-payment-loader-step')
  );
  if (!steps.length) return;
  let idx = 0;
  steps.forEach((el, i) => {
    el.classList.toggle('is-active', i === 0);
    el.classList.toggle('is-done', false);
  });
  checkoutPaymentLoadingTimer = setInterval(() => {
    if (idx < steps.length - 1) {
      steps[idx]?.classList.remove('is-active');
      steps[idx]?.classList.add('is-done');
      idx += 1;
      steps[idx]?.classList.add('is-active');
    }
  }, 480);
}

function openTelegramBotLink(url: string): void {
  const link = String(url || '').trim();
  if (!link) return;
  const tg = getTg();
  try {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(link);
      return;
    }
  } catch {
    /* ignore */
  }
  try {
    window.open(link, '_blank');
  } catch {
    /* ignore */
  }
}

function waitCheckoutPaymentMin(minMs: number, startedAt: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, Math.max(0, minMs - (Date.now() - startedAt)));
  });
}

function setCheckoutPaymentPhase(phase: CheckoutPaymentPhase): void {
  const stack = document.getElementById('checkout-payment-stack');
  const loading = document.getElementById('checkout-payment-loading');
  const ready = document.getElementById('checkout-payment-ready');
  const claiming = document.getElementById('checkout-payment-claiming');
  const claimed = document.getElementById('checkout-payment-claimed-done');
  const error = document.getElementById('checkout-payment-error');
  const bar = document.getElementById('checkout-payment-screen-bar');
  const back = document.getElementById('checkout-payment-back');
  const headerSub = document.getElementById('checkout-payment-header-sub');
  const claimBtn = document.getElementById('checkout-payment-claimed') as HTMLButtonElement | null;
  if (!stack) return;

  if (phase !== 'loading') stopCheckoutPaymentLoadingAnimation();

  stack.classList.remove(
    'checkout-payment-stack--loading',
    'checkout-payment-stack--ready',
    'checkout-payment-stack--claiming',
    'checkout-payment-stack--claimed',
    'checkout-payment-stack--error'
  );
  stack.classList.add(`checkout-payment-stack--${phase}`);

  const phases: Array<[CheckoutPaymentPhase, HTMLElement | null]> = [
    ['loading', loading],
    ['ready', ready],
    ['claiming', claiming],
    ['claimed', claimed],
    ['error', error]
  ];
  for (const [name, el] of phases) {
    if (!el) continue;
    const active = name === phase;
    el.hidden = !active;
    el.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  if (bar) bar.hidden = phase !== 'ready';
  if (back instanceof HTMLElement) {
    back.hidden = phase === 'loading' || phase === 'claiming';
  }
  if (claimBtn) {
    claimBtn.disabled = false;
    claimBtn.classList.remove('checkout-submit-btn--busy');
  }
  if (headerSub) {
    headerSub.textContent =
      phase === 'loading'
        ? 'Оформление заказа'
        : phase === 'ready'
          ? 'Перевод по СБП'
          : phase === 'claiming'
            ? 'Проверка оплаты'
            : phase === 'claimed'
              ? 'Готово'
              : 'Повторите попытку';
  }
}

function openCheckoutPaymentScreen(): void {
  setCheckoutPaymentPhase('loading');
  startCheckoutPaymentLoadingAnimation();
  showView('view-checkout-payment', 'right');
}

function showCheckoutPaymentReady(data: CheckoutOrderResponse): void {
  const orderId = String(data.orderId || '').trim();
  checkoutPaymentOrderId = orderId;
  const payment = data.payment;
  const totalRub = payment?.totalRub;
  const instructions = payment?.instructions;

  const idEl = document.getElementById('checkout-payment-order-id');
  const totalEl = document.getElementById('checkout-payment-total');
  const copyCommentBtn = document.getElementById('checkout-payment-copy-comment');

  if (idEl) idEl.textContent = `#${shortCheckoutOrderId(orderId)}`;
  if (totalEl) {
    totalEl.textContent =
      totalRub != null && Number.isFinite(totalRub) ? formatRubUi(totalRub) : '—';
  }

  renderCheckoutPaymentInstructions(instructions);

  if (copyCommentBtn) {
    copyCommentBtn.onclick = () => {
      void copyCheckoutText(
        String(instructions?.transferComment || ''),
        copyCommentBtn as HTMLButtonElement
      );
    };
  }

  setCheckoutPaymentPhase('ready');
}

function showCheckoutPaymentError(message: string): void {
  const errText = document.getElementById('checkout-payment-error-text');
  if (errText) errText.textContent = message;
  setCheckoutPaymentPhase('error');
}

async function claimCheckoutPayment(): Promise<void> {
  const orderId = String(checkoutPaymentOrderId || '').trim();
  if (!orderId) return;
  const btn = document.getElementById('checkout-payment-claimed') as HTMLButtonElement | null;
  setCheckoutPaymentPhase('claiming');
  const startedAt = Date.now();
  try {
    const data = await apiFetchJSON<ApiResponse>('/api/my-orders/claim-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId })
    });
    await waitCheckoutPaymentMin(CHECKOUT_CLAIM_MIN_MS, startedAt);
    if (data?.ok) {
      setCheckoutPaymentPhase('claimed');
      void loadMyOrders();
      return;
    }
    setCheckoutPaymentPhase('ready');
    showCheckoutPaymentInlineError(checkoutSubmitErrorMessage(data));
  } catch {
    setCheckoutPaymentPhase('ready');
    showCheckoutPaymentInlineError(checkoutSubmitErrorMessage(null));
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.classList.remove('checkout-submit-btn--busy');
    }
  }
}

function showCheckoutPaymentInlineError(message: string): void {
  const bar = document.getElementById('checkout-payment-screen-bar');
  if (!bar) return;
  let el = document.getElementById('checkout-payment-bar-error');
  if (!el) {
    el = document.createElement('p');
    el.id = 'checkout-payment-bar-error';
    el.className = 'checkout-payment-bar-error';
    bar.insertBefore(el, bar.firstChild);
  }
  el.textContent = message;
  el.hidden = false;
  window.setTimeout(() => {
    if (el) el.hidden = true;
  }, 5000);
}

function bindCheckoutPaymentAwaitActions(): void {
  document.getElementById('checkout-payment-claimed')?.addEventListener('click', () => {
    void claimCheckoutPayment();
  });
  document.getElementById('checkout-payment-retry')?.addEventListener('click', () => {
    showView('view-checkout', 'left');
  });
  document.getElementById('checkout-payment-go-orders')?.addEventListener('click', () => {
    showView('view-profile', 'left');
  });
}

export async function openOrderPaymentForOrder(orderId: string): Promise<void> {
  const oid = String(orderId || '').trim();
  if (!oid) return;
  openCheckoutPaymentScreen();
  const startedAt = Date.now();
  try {
    const data = await apiFetchJSON<CheckoutOrderResponse>('/api/my-orders/resend-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId: oid })
    });
    await waitCheckoutPaymentMin(CHECKOUT_PAYMENT_MIN_MS, startedAt);
    if (data?.ok) {
      showCheckoutPaymentReady({ ...data, orderId: data.orderId || oid });
      return;
    }
    showCheckoutPaymentError(
      checkoutSubmitErrorMessage(data, 'Не удалось открыть оплату. Попробуйте позже.')
    );
  } catch {
    await waitCheckoutPaymentMin(CHECKOUT_PAYMENT_MIN_MS, startedAt);
    showCheckoutPaymentError('Не удалось открыть оплату. Проверьте интернет.');
  }
}

function ensureCheckoutSubmitRequestId(): string {
  if (!checkoutSubmitRequestId) {
    checkoutSubmitRequestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return checkoutSubmitRequestId;
}

interface CheckoutViewElement extends HTMLElement {
  _checkoutStuckUpdate?: () => void;
}

function checkoutSubmitErrorMessage(
  data: ApiResponse | null | undefined,
  fallback = 'Не удалось оформить заказ. Попробуйте позже.'
): string {
  if (data && !data.ok && data.error) return String(data.error);
  return fallback;
}

function showCheckoutPopup(title: string, message: string): void {
  const tg = getTg();
  try {
    if (tg?.showPopup) {
      tg.showPopup({ title, message, buttons: [{ type: 'ok' }] });
      return;
    }
  } catch {
    /* ignore */
  }
  try {
    alert(message);
  } catch {
    /* ignore */
  }
}

export function initCheckoutUi(): void {
  const cityManualEl = document.getElementById('checkout-city-manual') as HTMLInputElement | null;
  const pickupManualEl = document.getElementById('checkout-pickup-manual') as HTMLInputElement | null;
  const cityHiddenEl = document.getElementById('checkout-city') as HTMLInputElement | null;
  const pickupHiddenEl = document.getElementById('checkout-pickup-point') as HTMLInputElement | null;
  const pickupLabelEl = document.getElementById('checkout-pickup-label');
  const pickupRevealWrap = document.getElementById('checkout-pickup-reveal-wrap');
  const firstNameEl = document.getElementById('checkout-first-name') as HTMLInputElement | null;
  const lastNameEl = document.getElementById('checkout-last-name') as HTMLInputElement | null;
  const phoneEl = document.getElementById('checkout-phone') as HTMLInputElement | null;
  const deliveryMethodEls = document.querySelectorAll(
    'input[name="checkout-delivery-method"]'
  ) as NodeListOf<HTMLInputElement>;
  let savedDeliveryCity = '';
  let lastDeliveryMethod = '';
  const selfPickupNoteCard = document.getElementById('checkout-selfpickup-note-card');
  const selfPickupRevealWrap = document.getElementById('checkout-selfpickup-reveal-wrap');
  const noteTextEl = document.getElementById('checkout-note-text');
  const originalCheckoutNoteText = noteTextEl ? String(noteTextEl.textContent || '') : '';

  function getCityValue(): string {
    if (isCheckoutCdekPickerMode()) {
      return cityHiddenEl ? String(cityHiddenEl.value || '').trim() : '';
    }
    return cityManualEl ? String(cityManualEl.value || '').trim() : '';
  }

  function getPickupValue(): string {
    if (isCheckoutCdekPickerMode()) {
      return pickupHiddenEl ? String(pickupHiddenEl.value || '').trim() : '';
    }
    return pickupManualEl ? String(pickupManualEl.value || '').trim() : '';
  }

  function getCityInputEl(): HTMLInputElement | null {
    return isCheckoutCdekPickerMode() ? cityHiddenEl : cityManualEl;
  }

  function getPickupInputEl(): HTMLInputElement | null {
    return isCheckoutCdekPickerMode() ? pickupHiddenEl : pickupManualEl;
  }

  function getCityTriggerEl(): HTMLElement | null {
    return document.getElementById('checkout-city-trigger');
  }

  function getPickupTriggerEl(): HTMLElement | null {
    return document.getElementById('checkout-pickup-trigger');
  }

  function getSelectedDeliveryMethod(): DeliveryMethod {
    let selected: DeliveryMethod = 'pickup';
    deliveryMethodEls.forEach((el) => {
      if (el.checked) selected = el.value === 'self-pickup' ? 'self-pickup' : 'pickup';
    });
    return selected;
  }

  function formatRuPhone(raw: string): string {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length && (digits[0] === '8' || digits[0] === '7')) digits = digits.slice(1);
    if (digits.length > 10) digits = digits.slice(0, 10);
    let out = '+7';
    if (!digits.length) return '';
    out += ' (' + digits.slice(0, 3);
    if (digits.length <= 3) return out;
    out += ') ' + digits.slice(3, 6);
    if (digits.length <= 6) return out;
    out += '-' + digits.slice(6, 8);
    if (digits.length <= 8) return out;
    out += '-' + digits.slice(8, 10);
    return out;
  }

  function bindCheckoutPhoneMask(): void {
    if (!phoneEl) return;
    function applyMask(): void {
      const formatted = formatRuPhone(phoneEl!.value);
      if (formatted !== phoneEl!.value) phoneEl!.value = formatted;
      syncCheckoutSubmitBtnState();
    }
    phoneEl.addEventListener('input', applyMask);
    phoneEl.addEventListener('change', applyMask);
    phoneEl.addEventListener('focus', () => {
      if (!String(phoneEl!.value || '').trim()) phoneEl!.value = '+7 ';
      syncCheckoutSubmitBtnState();
    });
    phoneEl.addEventListener('blur', () => {
      if (String(phoneEl!.value || '').trim() === '+7') phoneEl!.value = '';
      syncCheckoutSubmitBtnState();
    });
    phoneEl.addEventListener('paste', () => {
      setTimeout(applyMask, 0);
    });
  }

  function checkoutPhoneLooksComplete(el: HTMLInputElement | null): boolean {
    if (!el) return false;
    let digits = String(el.value || '').replace(/\D/g, '');
    if (digits.length && (digits[0] === '8' || digits[0] === '7')) digits = digits.slice(1);
    return digits.length === 10;
  }

  function isCheckoutFieldsValid(): boolean {
    if (!firstNameEl || !lastNameEl || !phoneEl) return false;
    if (!getCityValue()) return false;
    if (getSelectedDeliveryMethod() !== 'self-pickup') {
      if (!getPickupValue()) return false;
    }
    if (!String(firstNameEl.value || '').trim()) return false;
    if (!String(lastNameEl.value || '').trim()) return false;
    if (!checkoutPhoneLooksComplete(phoneEl)) return false;
    return true;
  }

  function syncCheckoutSubmitBtnState(): void {
    const btn = document.getElementById('checkout-submit-btn') as HTMLButtonElement | null;
    if (!btn) return;
    let ok = false;
    try {
      ok = isCheckoutFieldsValid();
    } catch {
      ok = false;
    }
    btn.classList.toggle('checkout-submit-btn--ready', !!ok);
    try {
      btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    } catch {
      /* ignore */
    }
  }

  function setFieldError(el: HTMLElement | null, hasError: boolean): void {
    if (!el) return;
    if (hasError) el.classList.add('form-input-error');
    else el.classList.remove('form-input-error');
  }

  function setPickupVisibility(isVisible: boolean): void {
    if (pickupRevealWrap) {
      if (isVisible) {
        pickupRevealWrap.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          pickupRevealWrap?.classList.add('checkout-pickup-reveal--open');
        });
      } else {
        pickupRevealWrap.classList.remove('checkout-pickup-reveal--open');
        pickupRevealWrap.setAttribute('aria-hidden', 'true');
      }
    } else if (pickupLabelEl && pickupManualEl) {
      const display = isVisible ? '' : 'none';
      pickupLabelEl.style.display = display;
      pickupManualEl.style.display = display;
    }
    const pickupPickers = document.getElementById('checkout-pickup-pickers');
    if (pickupPickers) pickupPickers.style.display = isVisible && isCheckoutCdekPickerMode() ? '' : 'none';
    if (!isVisible) {
      if (pickupManualEl) {
        pickupManualEl.value = '';
        setFieldError(pickupManualEl, false);
      }
      if (pickupHiddenEl) {
        pickupHiddenEl.value = '';
      }
      setFieldError(getPickupTriggerEl(), false);
    }
  }

  function syncPaymentDescReveal(): void {
    const wrapCard = document.getElementById('checkout-payment-desc-reveal-card');
    const wrapCash = document.getElementById('checkout-payment-desc-reveal-cash');
    const cardRadio = document.querySelector(
      'input[name="checkout-payment-method"][value="card"]'
    ) as HTMLInputElement | null;
    const cashRadio = document.querySelector(
      'input[name="checkout-payment-method"][value="cash"]'
    ) as HTMLInputElement | null;
    if (!wrapCard || !cardRadio) return;
    const cardSelected = !!cardRadio.checked;
    wrapCard.classList.toggle('checkout-payment-desc-reveal--open', cardSelected);
    if (wrapCash && cashRadio) {
      wrapCash.classList.toggle('checkout-payment-desc-reveal--open', !cardSelected);
    }
  }

  function syncCheckoutPaymentMethodForDelivery(): void {
    const selected = getSelectedDeliveryMethod();
    const cardRadio = document.querySelector(
      'input[name="checkout-payment-method"][value="card"]'
    ) as HTMLInputElement | null;
    const cashRadio = document.querySelector(
      'input[name="checkout-payment-method"][value="cash"]'
    ) as HTMLInputElement | null;
    const cashLabel = document.getElementById('checkout-payment-option-cash');
    if (!cardRadio) return;
    if (selected === 'self-pickup' && cashRadio && cashLabel) {
      cashLabel.hidden = false;
      cashRadio.checked = true;
      cardRadio.checked = false;
    } else {
      if (cashLabel) cashLabel.hidden = true;
      if (cashRadio) cashRadio.checked = false;
      cardRadio.checked = true;
    }
    syncPaymentDescReveal();
  }

  function applyDeliveryMethodState(): void {
    const selected = getSelectedDeliveryMethod();
    const isSelfPickup = selected === 'self-pickup';
    const switchedFromSelfToDelivery =
      lastDeliveryMethod === 'self-pickup' && selected !== 'self-pickup';
    lastDeliveryMethod = selected;

    if (isSelfPickup) {
      if (selfPickupRevealWrap) {
        selfPickupRevealWrap.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => {
          selfPickupRevealWrap?.classList.add('checkout-selfpickup-reveal--open');
        });
      }
      if (selfPickupNoteCard) {
        selfPickupNoteCard.classList.remove('checkout-note-card-expanded');
        selfPickupNoteCard.classList.add('checkout-note-card-show-more');
        const selfNoteText = selfPickupNoteCard.querySelector('.checkout-note-text') as HTMLElement | null;
        if (selfNoteText) selfNoteText.style.removeProperty('--checkout-note-max-h');
      }
      if (noteTextEl && originalCheckoutNoteText) {
        noteTextEl.textContent = originalCheckoutNoteText.replace(
          'готов к отправке',
          'готов к получению'
        );
      }
      if (cityManualEl) {
        const currentCity = String(cityManualEl.value || '').trim();
        if (currentCity && currentCity !== SELF_PICKUP_CITY) {
          savedDeliveryCity = currentCity;
        }
        cityManualEl.value = SELF_PICKUP_CITY;
        cityManualEl.disabled = true;
        cityManualEl.classList.add('is-muted');
        setFieldError(cityManualEl, false);
      }
      resetCheckoutCdekSelection();
      if (cityHiddenEl) {
        cityHiddenEl.value = SELF_PICKUP_CITY;
      }
      const cityTrigger = getCityTriggerEl();
      if (cityTrigger instanceof HTMLButtonElement) {
        cityTrigger.disabled = true;
        cityTrigger.classList.add('is-muted');
        if (isCheckoutCdekPickerMode()) {
          cityTrigger.textContent = SELF_PICKUP_CITY;
          cityTrigger.classList.remove('is-placeholder', 'form-input-error');
        }
      }
      setPickupVisibility(false);
      syncCheckoutPaymentMethodForDelivery();
      try {
        refreshCheckoutPaymentSummary();
      } catch {
        /* ignore */
      }
      try {
        syncCheckoutSubmitBtnState();
      } catch {
        /* ignore */
      }
      return;
    }

    if (selfPickupRevealWrap) {
      selfPickupRevealWrap.classList.remove('checkout-selfpickup-reveal--open');
      selfPickupRevealWrap.setAttribute('aria-hidden', 'true');
    }
    if (noteTextEl && originalCheckoutNoteText) {
      noteTextEl.textContent = originalCheckoutNoteText;
    }
    if (cityManualEl) {
      cityManualEl.disabled = false;
      cityManualEl.classList.remove('is-muted');
      if (switchedFromSelfToDelivery || String(cityManualEl.value || '').trim() === SELF_PICKUP_CITY) {
        cityManualEl.value = savedDeliveryCity || '';
      }
    }
    const cityTrigger = getCityTriggerEl();
    if (cityTrigger instanceof HTMLButtonElement) {
      cityTrigger.disabled = false;
      cityTrigger.classList.remove('is-muted');
    }
    setPickupVisibility(true);
    syncCheckoutPaymentMethodForDelivery();
    try {
      refreshCheckoutPaymentSummary();
    } catch {
      /* ignore */
    }
    try {
      syncCheckoutSubmitBtnState();
    } catch {
      /* ignore */
    }
  }

  if (cityManualEl) {
    cityManualEl.addEventListener('input', syncCheckoutSubmitBtnState);
    cityManualEl.addEventListener('change', syncCheckoutSubmitBtnState);
  }
  if (pickupManualEl) {
    pickupManualEl.addEventListener('input', syncCheckoutSubmitBtnState);
    pickupManualEl.addEventListener('change', syncCheckoutSubmitBtnState);
  }

  onCheckoutCdekChange(() => {
    syncCheckoutSubmitBtnState();
    try {
      if (!isBuildCheckoutMode()) refreshCheckoutPaymentSummary();
    } catch {
      /* ignore */
    }
  });

  void initCheckoutCdek().then(() => {
    applyDeliveryMethodState();
    try {
      refreshCheckoutPaymentSummary();
    } catch {
      /* ignore */
    }
    syncCheckoutSubmitBtnState();
  });

  deliveryMethodEls.forEach((el) => {
    el.addEventListener('change', () => {
      applyDeliveryMethodState();
      try {
        refreshCheckoutPaymentSummary();
      } catch {
        /* ignore */
      }
    });
  });
  applyDeliveryMethodState();

  bindCheckoutPhoneMask();

  if (firstNameEl) {
    firstNameEl.addEventListener('input', syncCheckoutSubmitBtnState);
    firstNameEl.addEventListener('change', syncCheckoutSubmitBtnState);
  }
  if (lastNameEl) {
    lastNameEl.addEventListener('input', syncCheckoutSubmitBtnState);
    lastNameEl.addEventListener('change', syncCheckoutSubmitBtnState);
  }

  document.querySelectorAll('input[name="checkout-payment-method"]').forEach((el) => {
    el.addEventListener('change', syncPaymentDescReveal);
  });
  try {
    syncCheckoutPaymentMethodForDelivery();
  } catch {
    /* ignore */
  }

  const noteCard = document.getElementById('checkout-timing-note-card');
  const noteMoreBtn = document.getElementById('checkout-note-more-btn');
  bindCheckoutNoteCardExpansion(noteCard, noteMoreBtn);
  bindCheckoutNoteCardExpansion(
    selfPickupNoteCard,
    document.getElementById('checkout-selfpickup-note-more-btn')
  );

  try {
    refreshCheckoutOrderSummary();
  } catch {
    /* ignore */
  }
  try {
    refreshCheckoutPaymentSummary();
  } catch {
    /* ignore */
  }

  const promoBtn = document.getElementById('checkout-pay-promo-btn');
  if (promoBtn) {
    promoBtn.addEventListener('click', () => {
      flashAppNotice('Промокоды скоро будут доступны.');
    });
  }

  const submitBtn = document.getElementById('checkout-submit-btn') as HTMLButtonElement | null;
  if (submitBtn) {
    submitBtn.addEventListener('click', async () => {
      const cityElClick = getCityInputEl();
      const pickupElClick = getPickupInputEl();
      const cityTrigger = getCityTriggerEl();
      const pickupTrigger = getPickupTriggerEl();
      const firstNameEl2 = document.getElementById('checkout-first-name') as HTMLInputElement | null;
      const lastNameEl2 = document.getElementById('checkout-last-name') as HTMLInputElement | null;
      const phoneElClick = document.getElementById('checkout-phone') as HTMLInputElement | null;
      const commentEl = document.getElementById('checkout-order-comment') as HTMLTextAreaElement | null;
      const orderComment = commentEl ? String(commentEl.value || '').trim() : '';
      let missing = false;

      if (firstNameEl2) firstNameEl2.classList.remove('form-input-error');
      if (lastNameEl2) lastNameEl2.classList.remove('form-input-error');
      if (phoneElClick) phoneElClick.classList.remove('form-input-error');
      setFieldError(cityManualEl, false);
      setFieldError(pickupManualEl, false);
      setFieldError(cityTrigger, false);
      setFieldError(pickupTrigger, false);

      if (!getCityValue()) {
        missing = true;
        if (isCheckoutCdekPickerMode()) setFieldError(cityTrigger, true);
        else setFieldError(cityManualEl, true);
      }
      if (getSelectedDeliveryMethod() !== 'self-pickup') {
        if (!getPickupValue()) {
          missing = true;
          if (isCheckoutCdekPickerMode()) setFieldError(pickupTrigger, true);
          else setFieldError(pickupManualEl, true);
        }
      }
      if (!firstNameEl2 || !String(firstNameEl2.value || '').trim()) {
        missing = true;
        if (firstNameEl2) firstNameEl2.classList.add('form-input-error');
      }
      if (!lastNameEl2 || !String(lastNameEl2.value || '').trim()) {
        missing = true;
        if (lastNameEl2) lastNameEl2.classList.add('form-input-error');
      }
      if (!checkoutPhoneLooksComplete(phoneElClick)) {
        missing = true;
        if (phoneElClick) phoneElClick.classList.add('form-input-error');
      }

      if (missing) {
        const tg = getTg();
        try {
          if (tg?.showPopup) {
            tg.showPopup({
              title: 'Заполните поля',
              message: 'Пожалуйста, заполните все обязательные поля со звездочкой.',
              buttons: [{ type: 'ok' }]
            });
            return;
          }
        } catch {
          /* ignore */
        }
        try {
          alert('Пожалуйста, заполните все обязательные поля со звездочкой.');
        } catch {
          /* ignore */
        }
        return;
      }

      if (isBuildCheckoutMode()) {
        const draft = getBuildOrderDraft();
        if (!draft?.tasks) {
          flashAppNotice('Заполните форму сборки');
          return;
        }
        const tgUser = getTg();
        const usernameTg =
          tgUser?.initDataUnsafe?.user?.username != null
            ? String(tgUser.initDataUnsafe.user.username)
            : '';
        const cdekSnap = getCheckoutCdekSnapshot();
        const payload = {
          tasks: draft.tasks,
          requestType: draft.requestType,
          city: getCityValue(),
          deliveryMethod: getSelectedDeliveryMethod(),
          pickupPoint: getPickupValue(),
          cdekCityCode: cdekSnap.cityCode,
          cdekPvzCode: cdekSnap.pvzCode || undefined,
          deliveryRub: cdekSnap.deliveryRub,
          firstName: String(firstNameEl2!.value || '').trim(),
          lastName: String(lastNameEl2!.value || '').trim(),
          phone: String(phoneElClick!.value || '').trim(),
          comment: orderComment,
          username_tg: usernameTg
        };
        submitBtn.disabled = true;
        setBuildSuccessPhase('loading');
        showView('view-build-success', 'right');
        const startedAt = Date.now();
        try {
          const res = await apiFetch('/api/build-request', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          let data: ApiResponse | null = null;
          try {
            data = (await res.json()) as ApiResponse;
          } catch {
            data = { ok: false };
          }
          await waitUntilMinElapsed(BUILD_ORDER_MIN_MS, startedAt);
          if (data?.ok) {
            recordBuildRequestSubmit();
            clearBuildOrderDraft();
            enterCatalogCheckout();
            setBuildSuccessPhase('done');
            void loadMyOrders();
            return;
          }
          setBuildSuccessPhase('done');
          showView('view-checkout', 'left');
          flashAppNotice(buildSubmitErrorMessage(data));
        } catch {
          await waitUntilMinElapsed(BUILD_ORDER_MIN_MS, startedAt);
          setBuildSuccessPhase('done');
          showView('view-checkout', 'left');
          flashAppNotice('Не удалось отправить заявку. Проверьте интернет.');
        } finally {
          submitBtn.disabled = false;
          syncCheckoutSubmitBtnState();
        }
        return;
      }

      const cart = loadCart();
      if (!cart.length) {
        flashAppNotice('Корзина пуста');
        return;
      }

      const paymentEl = document.querySelector(
        'input[name="checkout-payment-method"]:checked'
      ) as HTMLInputElement | null;
      const paymentMethod: PaymentMethod =
        paymentEl && paymentEl.value === 'cash' ? 'cash' : 'card';
      const usesPaymentScreen = paymentMethod !== 'cash';
      const tgUser = getTg();
      const usernameTg =
        tgUser?.initDataUnsafe?.user?.username != null
          ? String(tgUser.initDataUnsafe.user.username)
          : '';

      const cdekSnap = getCheckoutCdekSnapshot();
      const payload: CheckoutOrderPayload = {
        lines: cart.map((line) => ({
          id: line.id,
          title: line.title,
          price: line.price,
          oldPrice: line.oldPrice,
          qty: line.qty || 1,
          templateId: line.templateId,
          configChoices: line.configChoices,
          configSummary: line.configSummary
        })),
        city: getCityValue(),
        deliveryMethod: getSelectedDeliveryMethod(),
        pickupPoint: getPickupValue(),
        cdekCityCode: cdekSnap.cityCode,
        cdekPvzCode: cdekSnap.pvzCode || undefined,
        deliveryRub: cdekSnap.deliveryRub,
        firstName: String(firstNameEl2!.value || '').trim(),
        lastName: String(lastNameEl2!.value || '').trim(),
        phone: String(phoneElClick!.value || '').trim(),
        paymentMethod,
        comment: orderComment,
        username_tg: usernameTg,
        clientRequestId: ensureCheckoutSubmitRequestId()
      };

      submitBtn.disabled = true;
      if (usesPaymentScreen) openCheckoutPaymentScreen();
      const startedAt = Date.now();
      try {
        const res = await apiFetch('/api/checkout-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        let data: CheckoutOrderResponse | null = null;
        try {
          data = (await res.json()) as CheckoutOrderResponse;
        } catch {
          data = { ok: false };
        }
        if (usesPaymentScreen) {
          await waitCheckoutPaymentMin(CHECKOUT_PAYMENT_MIN_MS, startedAt);
        }
        if (data?.ok) {
          checkoutSubmitRequestId = '';
          clearCart();
          try {
            refreshCheckoutOrderSummary();
          } catch {
            /* ignore */
          }
          if (data.payment?.status === 'awaiting_payment') {
            showCheckoutPaymentReady(data);
            return;
          }
          if (usesPaymentScreen) {
            showView('view-checkout', 'left');
          }
          showCheckoutPopup(
            'Заказ принят',
            'Мы получили заказ и свяжемся с вами для уточнения деталей.'
          );
          return;
        }
        if (usesPaymentScreen) {
          showCheckoutPaymentError(checkoutSubmitErrorMessage(data));
        } else {
          showCheckoutPopup('Ошибка', checkoutSubmitErrorMessage(data));
        }
      } catch {
        if (usesPaymentScreen) {
          showCheckoutPaymentError(checkoutSubmitErrorMessage(null));
        } else {
          showCheckoutPopup('Ошибка', checkoutSubmitErrorMessage(null));
        }
      } finally {
        submitBtn.disabled = false;
        syncCheckoutSubmitBtnState();
      }
    });
  }

  try {
    syncCheckoutSubmitBtnState();
  } catch {
    /* ignore */
  }

  bindCheckoutPaymentAwaitActions();

  bindCheckoutSubmitBarStuck();
}

function bindCheckoutSubmitBarStuck(): void {
  const view = document.getElementById('view-checkout') as CheckoutViewElement | null;
  const main = view?.querySelector('.checkout-content');
  const bar = view?.querySelector('.checkout-bottom-bar');
  if (!view || !main || !bar) return;

  function viewportHeight(): number {
    try {
      if (window.visualViewport?.height) return window.visualViewport.height;
    } catch {
      /* ignore */
    }
    return window.innerHeight || 0;
  }

  function measureSubmitBar(): void {
    try {
      const h = Math.ceil(bar!.getBoundingClientRect().height);
      if (h > 0) view!.style.setProperty('--checkout-submit-bar-h', h + 'px');
    } catch {
      /* ignore */
    }
  }

  function updateStuck(): void {
    if (!view!.classList.contains('view-active')) return;
    measureSubmitBar();
    try {
      const vh = viewportHeight();
      const mainRect = main!.getBoundingClientRect();
      const barRect = bar!.getBoundingClientRect();
      const atPageEnd = mainRect.bottom <= vh + 6;
      const barPinnedToViewport = barRect.bottom >= vh - 10;
      const stuck = barPinnedToViewport && !atPageEnd;
      view!.classList.toggle('checkout-submit-bar-stuck', stuck);
    } catch {
      /* ignore */
    }
  }

  view._checkoutStuckUpdate = updateStuck;
  measureSubmitBar();
  updateStuck();

  let stuckRaf: number | null = null;
  function scheduleStuckUpdate(): void {
    if (stuckRaf != null) return;
    stuckRaf = requestAnimationFrame(() => {
      stuckRaf = null;
      updateStuck();
    });
  }

  try {
    document.addEventListener('scroll', scheduleStuckUpdate, { passive: true, capture: true });
    window.addEventListener('scroll', scheduleStuckUpdate, { passive: true });
    window.addEventListener('resize', scheduleStuckUpdate);
  } catch {
    /* ignore */
  }
  try {
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scheduleStuckUpdate);
      window.visualViewport.addEventListener('scroll', scheduleStuckUpdate, { passive: true });
    }
  } catch {
    /* ignore */
  }

  if (typeof ResizeObserver !== 'undefined') {
    try {
      const ro = new ResizeObserver(updateStuck);
      ro.observe(bar);
      ro.observe(main);
    } catch {
      /* ignore */
    }
  }
}
